import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Formik } from "formik";
import * as yup from "yup";
import ReCAPTCHA from "react-google-recaptcha";
import axios from "axios";

import { API_BASE_URL } from "../../config";
import AppButton from "../../design-system/components/AppButton";
import AppCallout from "../../design-system/components/AppCallout";
import AppField from "../../design-system/components/AppField";
import AppInput from "../../design-system/components/AppInput";
import AppLink from "../../design-system/components/AppLink";
import { IconChevronLeft } from "../../design-system/icons";
import { LoginSplit, MobileAppNotice } from "../login";
import "../../i18n";
import "../../design-system/styles/tokens.css";
import "../../design-system/styles/components.css";
import "../login/login.css";
import "./registration.css";

const RECAPTCHA_SITE_KEY = "6LeF74kqAAAAAFImIOT2yiXJuEJkcq8dU8vq6lKE";

// Einfacher UA-Check im Frontend: TimeTab blockt den mobilen Browser und
// verweist auf die App. Gleiche Logik wie auf /login.
const isMobileUA = () =>
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    (typeof navigator !== "undefined" ? navigator.userAgent : "") || ""
  );

const checkoutSchema = yup.object().shape({
  first_name: yup.string().required("erforderlich"),
  last_name: yup.string().required("erforderlich"),
  email: yup.string().email("ungültige Email").required("erforderlich"),
  company_name: yup
    .string()
    .required("erforderlich")
    .matches(
      /^[A-Za-z0-9 ]+$/,
      "Firmennamen darf nur Buchstaben (keine ä,ö,ü), Zahlen und Leerzeichen enthalten."
    ),
  phone_number: yup
    .string()
    .matches(/^004\d+$/, {
      message: "Telefonummer muss mit 004 starten und besteht nur aus Zahlen",
      excludeEmptyString: true,
    })
    .nullable()
    .notRequired(),
});

const initialValues = {
  first_name: "",
  last_name: "",
  email: "",
  employment_level: "",
  company_name: "",
  access_level: "",
  department: "",
  employment: "",
  phone_number: "",
};

const LEAD_MARKER_KEY = "timetab.registration_lead";

// Der Marker überlebt die Navigation auf die Dankesseite, aber nicht das
// Einlösen: So meldet jede abgeschlossene Registrierung genau einen Lead.
// Reload, Zurück-Button oder ein direkter Aufruf von /registration/success
// finden keinen Marker mehr und lösen daher kein zweites Event aus.
const markRegistrationLead = () => {
  try {
    window.sessionStorage.setItem(LEAD_MARKER_KEY, "1");
  } catch {
    // sessionStorage gesperrt — siehe consumeRegistrationLead.
  }
};

const consumeRegistrationLead = () => {
  try {
    if (window.sessionStorage.getItem(LEAD_MARKER_KEY) === null) return false;
    window.sessionStorage.removeItem(LEAD_MARKER_KEY);
    return true;
  } catch {
    // Kein sessionStorage (z. B. strenge Browser-Einstellungen): lieber
    // ungefiltert melden, als den Lead zu verlieren.
    return true;
  }
};

// Feldfehler nur zeigen, wenn das Feld schon berührt wurde.
const fieldError = (touched, errors, name) => (touched[name] && errors[name] ? errors[name] : "");

export function RegistrationSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const warning = location.state?.warning || "";

  const goToLogin = useCallback(() => {
    navigate(
      "/login",
      warning
        ? {
            state: {
              flash: warning,
              flashVariant: "warning",
            },
          }
        : undefined
    );
  }, [navigate, warning]);

  // Der Web-Client ist eine SPA: Beim Wechsel auf /registration/success lädt
  // keine neue Seite, daher feuert der GTM-Pageview-Trigger nicht. Der Lead
  // wird deshalb explizit in den dataLayer gepusht — aber nur, wenn das
  // Formular zuvor wirklich eine Registrierung abgeschlossen hat.
  useEffect(() => {
    if (!consumeRegistrationLead()) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: "registration_success" });
  }, []);

  useEffect(() => {
    const redirectTimer = window.setTimeout(goToLogin, 10000);
    return () => window.clearTimeout(redirectTimer);
  }, [goToLogin]);

  return (
    <LoginSplit>
      <div className="v2login__form v2login__form--wide v2login__fade-in">
        <div className="v2login__head">
          <h2 className="v2login__title">Vielen Dank für deine Registrierung.</h2>
          <p className="v2login__subtitle">
            Du wirst in 10 Sekunden automatisch zur Login-Seite weitergeleitet. Möchtest du nicht
            warten, kannst du{" "}
            <AppLink
              href="/login"
              onClick={(event) => {
                event.preventDefault();
                goToLogin();
              }}
            >
              direkt zur Login-Seite wechseln
            </AppLink>
            .
          </p>
        </div>

        {warning ? <AppCallout variant="warning" description={warning} /> : null}
      </div>
    </LoginSplit>
  );
}

function RegistrationForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [errorText, setErrorText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");

  const handleCaptchaChange = (value) => {
    setCaptchaToken(value || "");
    if (value) setErrorText("");
  };

  const handleCaptchaExpired = () => {
    setCaptchaToken("");
    setErrorText(
      "Die Sicherheitsprüfung ist abgelaufen. Bitte bestätige sie erneut."
    );
  };

  const handleFormSubmit = async (values, { resetForm, setFieldError }) => {
    if (!captchaToken) {
      setErrorText(
        "Bitte bestätige die Sicherheitsprüfung, bevor du dich registrierst."
      );
      return;
    }

    setErrorText("");
    setIsLoading(true);

    try {
      // --- 1) Vorab-Checks parallel
      const checks = [
        axios
          .post(`${API_BASE_URL}/api/web/registration/check-email`, { email: values.email })
          .catch(() => ({ data: {} })),
      ];
      const wantPhone = Boolean(values.phone_number);

      if (wantPhone) {
        checks.push(
          axios
            .post(`${API_BASE_URL}/api/web/registration/check-phone`, { phone_number: values.phone_number })
            .catch(() => ({ data: {} }))
        );
      }
      const results = await Promise.all(checks);

      const emailExists = results[0]?.data?.exists === true;
      const phoneExists = wantPhone ? results[1]?.data?.exists === true : false;

      const problems = [];
      if (emailExists) {
        const msg = t("registration.email_exist", "Diese E-Mail ist bereits registriert.");
        setFieldError("email", msg);
        problems.push(msg);
      }
      if (phoneExists) {
        const msg = t("registration.phone_exist", "Diese Telefonnummer ist bereits registriert.");
        setFieldError("phone_number", msg);
        problems.push(msg);
      }
      if (problems.length > 0) {
        setErrorText(problems.join(" • "));
        setIsLoading(false);
        return; // Registrierung NICHT ausführen
      }

      // --- 2) Registrierung nur wenn alles frei
      const registrationResponse = await axios.post(
        `${API_BASE_URL}/api/web/registration`,
        values,
        { headers: {} }
      );

      setIsLoading(false);
      resetForm();
      markRegistrationLead();
      navigate("/registration/success", {
        state: { warning: registrationResponse?.data?.warning || "" },
      });
    } catch (error) {
      // --- 3) Fallback: Serverseitige Meldungen priorisiert
      const msgs = [];
      const status = error?.response?.status;
      const data = error?.response?.data;
      const raw = typeof data === "string" ? data : JSON.stringify(data || {});

      const reason = error?.response?.data?.reason || "";
      if (status === 409) {
        if (reason === "EMAIL_EXISTS") {
          const msg = t("registration.email_exist", "Diese E-Mail ist bereits registriert.");
          setFieldError("email", msg);
          msgs.push(msg);
        } else if (reason === "PHONE_EXISTS") {
          const msg = t("registration.phone_exist", "Diese Telefonnummer ist bereits registriert.");
          setFieldError("phone_number", msg);
          msgs.push(msg);
        }
      }

      // Rohtext/HTML von IntegrityError parsen
      if (msgs.length === 0) {
        if (/phone_number/i.test(raw) || /ix_user_phone_number/i.test(raw)) {
          const msg = t("registration.phone_exist", "Diese Telefonnummer ist bereits registriert.");
          setFieldError("phone_number", msg);
          msgs.push(msg);
        } else if (/email/i.test(raw) || /ix_user_email/i.test(raw)) {
          const msg = t("registration.email_exist", "Diese E-Mail ist bereits registriert.");
          setFieldError("email", msg);
          msgs.push(msg);
        }
      }

      const backendMessage =
        typeof data === "object" && data
          ? data.message || data.error
          : "";
      setErrorText(
        msgs.length
          ? msgs.join(" • ")
          : backendMessage ||
              (error?.response
                ? t("registration.no_success", "Registrierung fehlgeschlagen.")
                : "Der Server konnte nicht erreicht werden. Bitte prüfe deine Verbindung und versuche es erneut.")
      );
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="v2login__back">
        <AppLink
          onClick={() => navigate("/login")}
          leadingIcon={<IconChevronLeft sx={{ fontSize: 16 }} />}
        >
          Zurück zum Login
        </AppLink>
      </div>

      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        validationSchema={checkoutSchema}
        validateOnMount
      >
        {({ values, errors, touched, handleBlur, handleChange, handleSubmit, isValid, dirty }) => (
          <form
            className="v2login__form v2login__form--wide v2login__fade-in"
            onSubmit={handleSubmit}
            aria-busy={isLoading}
          >
            <div className="v2login__head">
              <h2 className="v2login__title">
                {t("registration.headline", "Erstelle dein TimeTab-Konto.")}
              </h2>
              <p className="v2login__subtitle">
                Teste TimeTab mit deinem Unternehmen 30 Tage kostenlos.
              </p>
            </div>

            <div className="v2login__fields">
              <div className="v2reg__row">
                <AppField
                  label={t("registration.first_name", "Vorname")}
                  error={fieldError(touched, errors, "first_name")}
                  required
                >
                  <AppInput
                    name="first_name"
                    value={values.first_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    invalid={Boolean(fieldError(touched, errors, "first_name"))}
                    autoComplete="given-name"
                    placeholder="Kim"
                    required
                  />
                </AppField>
                <AppField
                  label={t("registration.last_name", "Nachname")}
                  error={fieldError(touched, errors, "last_name")}
                  required
                >
                  <AppInput
                    name="last_name"
                    value={values.last_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    invalid={Boolean(fieldError(touched, errors, "last_name"))}
                    autoComplete="family-name"
                    placeholder="Muster"
                    required
                  />
                </AppField>
              </div>

              <AppField
                label={t("registration.email_business", "Geschäftliche E-Mail")}
                error={fieldError(touched, errors, "email")}
                required
              >
                <AppInput
                  type="email"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  invalid={Boolean(fieldError(touched, errors, "email"))}
                  autoComplete="email"
                  placeholder="name@firma.ch"
                  required
                />
              </AppField>

              <AppField
                className="v2reg__field--optional"
                label={
                  <>
                    {t("registration.phone_number", "Telefonnummer")}
                    <span className="v2reg__optional">optional</span>
                  </>
                }
                error={fieldError(touched, errors, "phone_number")}
              >
                {/* Die Prüfung verlangt 004 + reine Ziffern — der Platzhalter zeigt
                    darum kein "+41" und keine Leerzeichen. */}
                <AppInput
                  type="tel"
                  name="phone_number"
                  value={values.phone_number}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  invalid={Boolean(fieldError(touched, errors, "phone_number"))}
                  autoComplete="tel"
                  placeholder="0041790000000"
                />
              </AppField>

              <AppField
                label={t("registration.company_name", "Firmenname")}
                error={fieldError(touched, errors, "company_name")}
                required
              >
                <AppInput
                  name="company_name"
                  value={values.company_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  invalid={Boolean(fieldError(touched, errors, "company_name"))}
                  autoComplete="organization"
                  placeholder="Muster AG"
                  required
                />
              </AppField>
            </div>

            <div className="v2reg__captcha">
              <ReCAPTCHA
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={handleCaptchaChange}
                onExpired={handleCaptchaExpired}
                onErrored={() => {
                  setCaptchaToken("");
                  setErrorText(
                    "Die Sicherheitsprüfung konnte nicht geladen werden. Bitte lade die Seite neu."
                  );
                }}
              />
            </div>

            {errorText ? (
              <AppCallout
                variant="error"
                description={errorText}
                style={{ marginTop: "var(--ds-space-16)" }}
              />
            ) : null}

            <div className="v2login__actions">
              <AppButton
                type="submit"
                variant="primary"
                size="lg"
                className="v2login__submit"
                disabled={isLoading || !captchaToken || !isValid || !dirty}
              >
                {isLoading ? "Wird registriert…" : "Registriere dich jetzt kostenlos"}
              </AppButton>
            </div>

            <p className="v2reg__note">
              30 Tage kostenlos · Keine Kreditkarte nötig · Jederzeit kündbar
            </p>

            <div className="v2login__footnote">
              <span className="v2reg__account-hint">
                Bereits ein Konto?{" "}
                <AppLink href="/login">Anmelden</AppLink>
              </span>
            </div>
          </form>
        )}
      </Formik>
    </>
  );
}

export default function Registration() {
  const mobileBlocked = useMemo(() => isMobileUA(), []);

  return (
    <LoginSplit>
      {mobileBlocked ? (
        <MobileAppNotice
          title="Bitte registriere dich am Computer"
          subtitle="Die Registrierung über den mobilen Browser ist deaktiviert."
        />
      ) : (
        <RegistrationForm />
      )}
    </LoginSplit>
  );
}
